import { useState, useMemo, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Loader2,
  Trash2,
  Wifi,
  XCircle,
} from 'lucide-react';
import { UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useProperties, useUnits } from '@/hooks/api/useProperties';
import {
  useCreateConnection,
  useTestConnection,
  useUnitMappings,
  useCreateUnitMapping,
  useDeleteUnitMapping,
} from '@/hooks/api/useChannels';
import { getAirbnbOAuthUrl } from '@/services/api/channels.api';

// ---- Types ----

type Channel = 'airbnb' | 'booking_com' | 'expedia' | 'axis_rooms';

interface ChannelOption {
  value: Channel;
  label: string;
  bgColor: string;
  textColor: string;
}

const CHANNELS: ChannelOption[] = [
  { value: 'axis_rooms', label: 'AxisRooms', bgColor: 'bg-emerald-600', textColor: 'text-white' },
  { value: 'airbnb', label: 'Airbnb', bgColor: 'bg-rose-500', textColor: 'text-white' },
  { value: 'booking_com', label: 'Booking.com', bgColor: 'bg-blue-600', textColor: 'text-white' },
  { value: 'expedia', label: 'Expedia', bgColor: 'bg-yellow-500', textColor: 'text-white' },
];

/** Parse a comma/space-separated rate-plan list ("1, 2") into ["1","2"]. */
function parseRatePlanIds(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const STEP_LABELS = [
  'Select Channel',
  'Credentials',
  'External Property',
  'Test Connection',
  'Map Units',
] as const;

// ---- Step Components ----

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <nav aria-label="Setup progress" className="flex items-center gap-2">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold border-2 transition-colors',
              i < current
                ? 'bg-primary-600 border-primary-600 text-white'
                : i === current
                  ? 'border-primary-600 text-primary-600 bg-white'
                  : 'border-gray-200 text-gray-400 bg-white',
            )}
            aria-current={i === current ? 'step' : undefined}
          >
            {i < current ? <CheckCircle2 size={14} /> : i + 1}
          </div>
          {i < total - 1 && (
            <div
              className={cn(
                'hidden sm:block w-8 h-0.5',
                i < current ? 'bg-primary-600' : 'bg-gray-200',
              )}
            />
          )}
        </div>
      ))}
    </nav>
  );
}

function StepSelectChannel({
  selected,
  propertyId,
  properties,
  onSelectChannel,
  onSelectProperty,
}: {
  selected: Channel | null;
  propertyId: string;
  properties: Array<Record<string, unknown>>;
  onSelectChannel: (ch: Channel) => void;
  onSelectProperty: (id: string) => void;
}) {
  const selectId = useId();

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor={selectId} className="block text-sm font-medium text-gray-700 mb-2">
          Property
        </label>
        <div className="relative">
          <select
            id={selectId}
            value={propertyId}
            onChange={(e) => onSelectProperty(e.target.value)}
            className={cn(
              'w-full max-w-sm appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            )}
          >
            <option value="">Select a property...</option>
            {properties.map((p) => (
              <option key={p.id as string} value={p.id as string}>
                {p.name as string}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            aria-hidden="true"
          />
        </div>
      </div>

      <fieldset>
        <legend className="text-sm font-medium text-gray-700 mb-3">Select a Channel</legend>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {CHANNELS.map((ch) => (
            <button
              key={ch.value}
              type="button"
              onClick={() => onSelectChannel(ch.value)}
              className={cn(
                'flex flex-col items-center gap-3 rounded-xl border-2 p-6 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                selected === ch.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 bg-white hover:border-gray-300',
              )}
              aria-pressed={selected === ch.value}
            >
              <div
                className={cn(
                  'flex items-center justify-center w-14 h-14 rounded-xl font-bold text-sm',
                  ch.bgColor,
                  ch.textColor,
                )}
                aria-hidden="true"
              >
                {ch.label.slice(0, 3).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-gray-800">{ch.label}</span>
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

function StepCredentials({
  channel,
  username,
  password,
  ratePlanIds,
  onChangeUsername,
  onChangePassword,
  onChangeRatePlanIds,
  onOAuth,
  oauthLoading,
}: {
  channel: Channel;
  username: string;
  password: string;
  ratePlanIds: string;
  onChangeUsername: (v: string) => void;
  onChangePassword: (v: string) => void;
  onChangeRatePlanIds: (v: string) => void;
  onOAuth: () => void;
  oauthLoading: boolean;
}) {
  const usernameId = useId();
  const passwordId = useId();
  const ratePlanId = useId();

  if (channel === 'axis_rooms') {
    return (
      <div className="space-y-4 max-w-sm">
        <p className="text-sm text-gray-600">
          Enter this hotel&apos;s AxisRooms rate-plan IDs. You&apos;ll enter the Hotel ID on
          the next step, and map each room to its AxisRooms room ID after that.
        </p>
        <div>
          <label htmlFor={ratePlanId} className="block text-xs font-medium text-gray-600 mb-1">
            Rate plan IDs
          </label>
          <input
            id={ratePlanId}
            type="text"
            value={ratePlanIds}
            onChange={(e) => onChangeRatePlanIds(e.target.value)}
            placeholder="e.g. 1, 2"
            className={cn(
              'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800',
              'placeholder:text-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            )}
          />
          <p className="mt-1 text-xs text-gray-400">
            Comma-separated, from AxisRooms (e.g. &quot;1, 2&quot;).
          </p>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-blue-500" aria-hidden="true" />
          <span>
            The AxisRooms <strong>access key</strong> and <strong>channel</strong> are the shared
            BestAcco keys — managed centrally, nothing to enter here. You only need the Hotel ID,
            rate-plan IDs, and room IDs (from AxisRooms).
          </span>
        </div>
      </div>
    );
  }

  if (channel === 'airbnb') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Airbnb uses OAuth for authentication. Click the button below to connect your Airbnb
          account.
        </p>
        <button
          type="button"
          onClick={onOAuth}
          disabled={oauthLoading}
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            oauthLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-rose-500 hover:bg-rose-600',
          )}
        >
          {oauthLoading ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <ExternalLink size={16} aria-hidden="true" />
          )}
          {oauthLoading ? 'Redirecting...' : 'Connect with Airbnb'}
        </button>
      </div>
    );
  }

  const channelLabel = channel === 'booking_com' ? 'Booking.com' : 'Expedia';

  return (
    <div className="space-y-4 max-w-sm">
      <p className="text-sm text-gray-600">
        Enter your {channelLabel} API credentials.
      </p>
      <div>
        <label htmlFor={usernameId} className="block text-xs font-medium text-gray-600 mb-1">
          Username
        </label>
        <input
          id={usernameId}
          type="text"
          value={username}
          onChange={(e) => onChangeUsername(e.target.value)}
          className={cn(
            'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          )}
          autoComplete="username"
        />
      </div>
      <div>
        <label htmlFor={passwordId} className="block text-xs font-medium text-gray-600 mb-1">
          Password
        </label>
        <input
          id={passwordId}
          type="password"
          value={password}
          onChange={(e) => onChangePassword(e.target.value)}
          className={cn(
            'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          )}
          autoComplete="current-password"
        />
      </div>
    </div>
  );
}

function StepExternalProperty({
  channel,
  externalPropertyId,
  poolSize,
  sandboxMode,
  onChange,
  onChangePoolSize,
  onToggleSandbox,
}: {
  channel: Channel;
  externalPropertyId: string;
  poolSize: string;
  sandboxMode: boolean;
  onChange: (v: string) => void;
  onChangePoolSize: (v: string) => void;
  onToggleSandbox: () => void;
}) {
  const inputId = useId();
  const poolSizeId = useId();
  const sandboxId = useId();
  const isAxis = channel === 'axis_rooms';

  return (
    <div className="space-y-4 max-w-sm">
      <div>
        <label htmlFor={inputId} className="block text-xs font-medium text-gray-600 mb-1">
          {isAxis ? 'Hotel ID' : 'External Property ID'}
        </label>
        <input
          id={inputId}
          type="text"
          value={externalPropertyId}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isAxis ? 'e.g. 234' : 'e.g. 12345678'}
          className={cn(
            'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800',
            'placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          )}
        />
        <p className="mt-1 text-xs text-gray-400">
          {isAxis
            ? 'This hotel’s AxisRooms Hotel ID (from AxisRooms).'
            : 'The property ID from the OTA platform.'}
        </p>
      </div>
      {isAxis && (
        <div>
          <label htmlFor={poolSizeId} className="block text-xs font-medium text-gray-600 mb-1">
            Shared room pool size <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id={poolSizeId}
            type="number"
            min={1}
            step={1}
            value={poolSize}
            onChange={(e) => onChangePoolSize(e.target.value)}
            placeholder="leave blank unless room types share a pool"
            className={cn(
              'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800',
              'placeholder:text-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            )}
          />
          <p className="mt-1 text-xs text-gray-400">
            Only for a property whose room types share one physical pool of rooms
            (esp. with different per-type caps). Set the total physical room count.
            Leave blank for independent per-room inventory.
          </p>
        </div>
      )}
      {/* AxisRooms targets prod vs sandbox via server config (AXIS_ROOMS_API_URL),
          not a per-connection flag, so the sandbox toggle is hidden for it. */}
      {!isAxis && (
        <label
          htmlFor={sandboxId}
          className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
        >
          <input
            id={sandboxId}
            type="checkbox"
            checked={sandboxMode}
            onChange={onToggleSandbox}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Sandbox / test mode
        </label>
      )}
    </div>
  );
}

function StepTestConnection({
  channel,
  testStatus,
  testMessage,
  onTest,
  isTesting,
}: {
  channel: Channel;
  testStatus: 'idle' | 'success' | 'error';
  testMessage: string;
  onTest: () => void;
  isTesting: boolean;
}) {
  // AxisRooms exposes no credential-test endpoint (its APIs are push-only and
  // need a mapped room), so a "test" here would falsely report "No room ID
  // mapped". Show guidance instead — the first availability sync confirms it.
  if (channel === 'axis_rooms') {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 px-3 py-3 text-sm text-blue-800">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-blue-500" aria-hidden="true" />
          <div>
            <p className="font-medium">Connection saved.</p>
            <p className="mt-1 text-xs text-blue-700">
              AxisRooms has no test endpoint, so there’s nothing to run here.
              Continue to map your rooms — then use <strong>Sync Now</strong> on the
              Channel Manager to push the first availability, which confirms the
              connection end to end.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Test the connection to verify your credentials and external property ID are correct.
      </p>
      <button
        type="button"
        onClick={onTest}
        disabled={isTesting}
        className={cn(
          'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          isTesting ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700',
        )}
      >
        {isTesting ? (
          <Loader2 size={14} className="animate-spin" aria-hidden="true" />
        ) : (
          <Wifi size={14} aria-hidden="true" />
        )}
        {isTesting ? 'Testing...' : 'Test Connection'}
      </button>

      {testStatus === 'success' && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" role="status">
          <CheckCircle2 size={16} aria-hidden="true" />
          {testMessage}
        </div>
      )}
      {testStatus === 'error' && (
        <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg" role="alert">
          <XCircle size={16} aria-hidden="true" />
          {testMessage}
        </div>
      )}
    </div>
  );
}

function StepMapUnits({
  channel,
  connectionId,
  units,
}: {
  channel: Channel;
  connectionId: string;
  units: Array<Record<string, unknown>>;
}) {
  const [unitId, setUnitId] = useState('');
  const [externalListingId, setExternalListingId] = useState('');
  const [allotment, setAllotment] = useState('1');
  const unitSelectId = useId();
  const listingInputId = useId();
  const allotmentInputId = useId();
  const isAxis = channel === 'axis_rooms';
  const listingLabel = isAxis ? 'AxisRooms Room ID' : 'External Listing ID';

  const { data: mappingsData, isLoading: mappingsLoading } = useUnitMappings(connectionId);
  const createMapping = useCreateUnitMapping();
  const deleteMapping = useDeleteUnitMapping();

  const mappings: Array<Record<string, unknown>> = useMemo(() => {
    if (!mappingsData) return [];
    return Array.isArray(mappingsData) ? mappingsData : (mappingsData.data ?? []);
  }, [mappingsData]);

  function handleAddMapping() {
    if (!unitId || !externalListingId) return;
    const parsedAllotment = Number.parseInt(allotment, 10);
    const allotmentValue =
      Number.isFinite(parsedAllotment) && parsedAllotment >= 1
        ? parsedAllotment
        : 1;
    createMapping.mutate(
      { connectionId, data: { unitId, externalListingId, allotment: allotmentValue } },
      {
        onSuccess: () => {
          setUnitId('');
          setExternalListingId('');
          setAllotment('1');
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        {isAxis
          ? 'Map each room to its AxisRooms room ID (from AxisRooms). This is what lets bookings and availability sync to the right room.'
          : 'Map your property units to external listing IDs on the OTA platform.'}
      </p>

      <div className="flex flex-col sm:flex-row gap-3 items-end">
        <div className="flex-1">
          <label htmlFor={unitSelectId} className="block text-xs font-medium text-gray-600 mb-1">
            Unit
          </label>
          <div className="relative">
            <select
              id={unitSelectId}
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              className={cn(
                'w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-800',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              )}
            >
              <option value="">Select unit...</option>
              {units.map((u) => (
                <option key={u.id as string} value={u.id as string}>
                  {(u.name as string) ?? (u.id as string)}
                </option>
              ))}
            </select>
            <ChevronDown
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              aria-hidden="true"
            />
          </div>
        </div>
        <div className="flex-1">
          <label htmlFor={listingInputId} className="block text-xs font-medium text-gray-600 mb-1">
            {listingLabel}
          </label>
          <input
            id={listingInputId}
            type="text"
            value={externalListingId}
            onChange={(e) => setExternalListingId(e.target.value)}
            placeholder={isAxis ? 'e.g. 3' : 'e.g. L-98765'}
            className={cn(
              'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800',
              'placeholder:text-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            )}
          />
        </div>
        <div className="w-full sm:w-28">
          <label htmlFor={allotmentInputId} className="block text-xs font-medium text-gray-600 mb-1">
            Rooms
          </label>
          <input
            id={allotmentInputId}
            type="number"
            min={1}
            step={1}
            value={allotment}
            onChange={(e) => setAllotment(e.target.value)}
            placeholder="e.g. 10"
            aria-describedby={`${allotmentInputId}-help`}
            className={cn(
              'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800',
              'placeholder:text-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            )}
          />
        </div>
        <button
          type="button"
          onClick={handleAddMapping}
          disabled={!unitId || !externalListingId || createMapping.isPending}
          className={cn(
            'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            !unitId || !externalListingId || createMapping.isPending
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700',
          )}
        >
          {createMapping.isPending ? 'Adding...' : 'Add Mapping'}
        </button>
      </div>

      <p id={`${allotmentInputId}-help`} className="text-xs text-gray-500">
        <strong>Rooms</strong> = how many rooms of this type the channel may sell
        (the real count, e.g. 10) — availability is pushed as this number. Use 1
        for a single room.
      </p>

      {mappingsLoading ? (
        <div className="flex items-center gap-2 py-4">
          <Loader2 size={16} className="animate-spin text-primary-600" aria-hidden="true" />
          <span className="text-sm text-gray-500">Loading mappings...</span>
        </div>
      ) : mappings.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100" aria-label="Unit mappings">
            <thead>
              <tr className="bg-gray-50">
                <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  Unit
                </th>
                <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">
                  {listingLabel}
                </th>
                <th scope="col" className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                  Rooms
                </th>
                <th scope="col" className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mappings.map((m) => {
                const mappedUnit = units.find((u) => u.id === m.unitId);
                return (
                  <tr key={m.id as string} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-gray-800">
                      {(mappedUnit?.name as string) ?? (m.unitId as string)}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 font-mono">
                      {m.externalListingId as string}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 text-right tabular-nums">
                      {(m.allotment as number) ?? 1}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() =>
                          deleteMapping.mutate({
                            connectionId,
                            mappingId: m.id as string,
                          })
                        }
                        disabled={deleteMapping.isPending}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
                        aria-label={`Remove mapping for ${(mappedUnit?.name as string) ?? (m.unitId as string)}`}
                      >
                        <Trash2 size={12} aria-hidden="true" />
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-400 py-2">No mappings yet. Add one above.</p>
      )}
    </div>
  );
}

// ---- Page ----

export function ChannelConnectionSetupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;

  const { data: propertiesData } = useProperties();
  const properties: Array<Record<string, unknown>> = useMemo(() => {
    if (!propertiesData) return [];
    return Array.isArray(propertiesData) ? propertiesData : (propertiesData.data ?? []);
  }, [propertiesData]);

  // ---- Form state ----
  const [step, setStep] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [ratePlanIds, setRatePlanIds] = useState('');
  const [externalPropertyId, setExternalPropertyId] = useState('');
  const [poolSize, setPoolSize] = useState('');
  const [sandboxMode, setSandboxMode] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [connectionId, setConnectionId] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [testMessage, setTestMessage] = useState('');

  const createConnection = useCreateConnection();
  const testConnectionMutation = useTestConnection();

  // Fetch units for the selected property via dedicated endpoint
  const { data: unitsData } = useUnits(propertyId || undefined);
  const units: Array<Record<string, unknown>> = useMemo(() => {
    if (!unitsData) return [];
    return Array.isArray(unitsData) ? unitsData : (unitsData.data ?? []);
  }, [unitsData]);

  // ---- Navigation guards ----
  function canProceed(): boolean {
    switch (step) {
      case 0:
        return !!selectedChannel && !!propertyId;
      case 1:
        if (selectedChannel === 'airbnb') return true; // OAuth handled externally
        if (selectedChannel === 'axis_rooms')
          return parseRatePlanIds(ratePlanIds).length > 0;
        return !!username && !!password;
      case 2:
        return !!externalPropertyId;
      case 3:
        return true; // test is optional
      case 4:
        return true;
      default:
        return false;
    }
  }

  async function handleNext() {
    if (step === 2 && !connectionId) {
      // Create connection before moving to test step
      const credentials =
        selectedChannel === 'airbnb'
          ? undefined
          : selectedChannel === 'axis_rooms'
            ? {
                // accessKey + channelId are the shared BestAcco keys (applied
                // server-side); only hotelId + ratePlanIds vary per hotel.
                hotelId: externalPropertyId.trim(),
                ratePlanIds: parseRatePlanIds(ratePlanIds),
              }
            : { username, password };

      // Optional shared-pool size (only meaningful for a shared-inventory
      // property, esp. with different per-type caps). Blank ⇒ omitted ⇒ default
      // per-listing behaviour, unchanged.
      const parsedPool = Number.parseInt(poolSize, 10);
      const poolSizeValue =
        Number.isFinite(parsedPool) && parsedPool >= 1 ? parsedPool : undefined;

      try {
        const result = await createConnection.mutateAsync({
          propertyId,
          channel: selectedChannel!,
          externalPropertyId,
          credentials,
          sandboxMode,
          ...(poolSizeValue !== undefined && { poolSize: poolSizeValue }),
        });
        const newId = (result?.id ?? result?.data?.id ?? '') as string;
        setConnectionId(newId);
        setStep((s) => s + 1);
      } catch {
        // mutation error is handled by React Query
      }
      return;
    }
    setStep((s) => s + 1);
  }

  async function handleOAuth() {
    setOauthLoading(true);
    try {
      const result = await getAirbnbOAuthUrl();
      const url = (result?.url ?? result?.data?.url ?? '') as string;
      if (url) {
        window.location.href = url;
      }
    } catch {
      setOauthLoading(false);
    }
  }

  function handleTest() {
    if (!connectionId) return;
    setTestStatus('idle');
    testConnectionMutation.mutate(connectionId, {
      onSuccess: () => {
        setTestStatus('success');
        setTestMessage('Connection test passed successfully.');
      },
      onError: (err) => {
        setTestStatus('error');
        setTestMessage(
          (err as Error).message || 'Connection test failed. Please check your credentials.',
        );
      },
    });
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-white py-24 px-6 text-center gap-4">
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-50">
          <XCircle size={32} className="text-red-400" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-700">Access Denied</h2>
          <p className="mt-1 text-sm text-gray-400 max-w-sm">
            Only Admins can set up new channel connections.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/channel-manager')}
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          aria-label="Back to Channel Manager"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back
        </button>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Connect Channel</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Step {step + 1} of {STEP_LABELS.length}: {STEP_LABELS[step]}
          </p>
        </div>
      </div>

      {/* Progress */}
      <StepIndicator current={step} total={STEP_LABELS.length} />

      {/* Step content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {step === 0 && (
          <StepSelectChannel
            selected={selectedChannel}
            propertyId={propertyId}
            properties={properties}
            onSelectChannel={setSelectedChannel}
            onSelectProperty={setPropertyId}
          />
        )}
        {step === 1 && selectedChannel && (
          <StepCredentials
            channel={selectedChannel}
            username={username}
            password={password}
            ratePlanIds={ratePlanIds}
            onChangeUsername={setUsername}
            onChangePassword={setPassword}
            onChangeRatePlanIds={setRatePlanIds}
            onOAuth={handleOAuth}
            oauthLoading={oauthLoading}
          />
        )}
        {step === 2 && (
          <StepExternalProperty
            channel={selectedChannel!}
            externalPropertyId={externalPropertyId}
            poolSize={poolSize}
            sandboxMode={sandboxMode}
            onChange={setExternalPropertyId}
            onChangePoolSize={setPoolSize}
            onToggleSandbox={() => setSandboxMode((v) => !v)}
          />
        )}
        {step === 3 && (
          <StepTestConnection
            channel={selectedChannel!}
            testStatus={testStatus}
            testMessage={testMessage}
            onTest={handleTest}
            isTesting={testConnectionMutation.isPending}
          />
        )}
        {step === 4 && connectionId && selectedChannel && (
          <StepMapUnits channel={selectedChannel} connectionId={connectionId} units={units} />
        )}

        {/* Error from create connection */}
        {createConnection.isError && (
          <div className="mt-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg" role="alert">
            <XCircle size={16} aria-hidden="true" />
            Failed to create connection. Please try again.
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className={cn(
            'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            step === 0
              ? 'border-gray-200 text-gray-300 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50',
          )}
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Previous
        </button>

        {step < STEP_LABELS.length - 1 ? (
          <button
            type="button"
            onClick={handleNext}
            disabled={!canProceed() || createConnection.isPending}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              !canProceed() || createConnection.isPending
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700',
            )}
          >
            {createConnection.isPending ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <ArrowRight size={14} aria-hidden="true" />
            )}
            {createConnection.isPending ? 'Creating...' : 'Next'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate('/channel-manager')}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              'bg-primary-600 hover:bg-primary-700',
            )}
          >
            <CheckCircle2 size={14} aria-hidden="true" />
            Done
          </button>
        )}
      </div>
    </div>
  );
}
